import React, { useEffect, useMemo, useState } from 'react';
import {
  Trash2, RotateCcw, Search,
  Users, Ticket, Package, Truck, FileSignature, FolderOpen, FileInput, FileText,
} from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

// Papelera unificada (estilo Vtiger): lista plana de elementos borrados
// de las entidades soportadas en el backend (`server/lib/softDelete.js`).
// Filtros: por entidad y por texto. Una sola acción: Restaurar.
// Sin "vaciar papelera" desde aquí: la decisión del usuario fue NO purgar.

const ENTITIES = [
  { key: '',                label: 'Todas las categorías' },
  { key: 'customer',        label: 'Clientes' },
  { key: 'ticket',          label: 'Tickets' },
  { key: 'product',         label: 'Productos' },
  { key: 'provider',        label: 'Proveedores' },
  { key: 'contract',        label: 'Contratos' },
  { key: 'budget',          label: 'Presupuestos' },
  { key: 'receivedInvoice', label: 'Facturas recibidas' },
];

// Icono por entidad — los mismos que el sidebar (Layout.js) para que el
// usuario reconozca de un vistazo de qué tipo es cada elemento borrado.
const ENTITY_ICON = {
  customer:        Users,
  ticket:          Ticket,
  product:         Package,
  provider:        Truck,
  contract:        FileSignature,
  budget:          FolderOpen,
  receivedInvoice: FileInput,
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const Trash = () => {
  const [items, setItems] = useState([]);
  const [filterEntity, setFilterEntity] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useViewMode('trash');
  const fl = useFieldLayout('trash', viewMode);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const modal = useModal();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/trash', { params: filterEntity ? { entity: filterEntity } : {} });
      setItems(res.data?.items || []);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error cargando papelera');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterEntity]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return items;
    return items.filter((it) =>
      String(it.title || '').toLowerCase().includes(q)
      || String(it.label || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const handleRestore = (item) => {
    const key = `${item.entity_type}-${item.id}`;
    modal.confirm(
      `¿Restaurar ${item.label.toLowerCase()} "${item.title}"? Volverá a aparecer en su listado.`,
      async () => {
        setRestoring(key);
        try {
          await api.post(`/trash/${item.entity_type}/${item.id}/restore`);
          setItems((prev) => prev.filter((p) => !(p.entity_type === item.entity_type && p.id === item.id)));
          modal.success(`${item.label} restaurado.`);
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error restaurando');
        } finally {
          setRestoring(null);
        }
      }
    );
  };

  const subtitle = items.length === 0
    ? 'No hay elementos eliminados'
    : `${items.length} elemento${items.length === 1 ? '' : 's'} eliminado${items.length === 1 ? '' : 's'} · puedes restaurarlos en cualquier momento`;

  const columns = [
    { key: 'label', header: 'Tipo', render: (it) => <span className="badge badge-gray">{it.label || '—'}</span> },
    { key: 'title', header: 'Nombre', className: 'dt-strong', fixed: true, render: (it) => it.title || '—' },
    { key: 'id', header: 'ID', className: 'dt-nowrap', render: (it) => `#${it.id}` },
    { key: 'invoice_count', header: 'Facturas', className: 'num', render: (it) => (
      it.entity_type === 'customer' && Number.isInteger(it.invoice_count) ? it.invoice_count : '—'
    ) },
    { key: 'deleted_at', header: 'Fecha de borrado', className: 'dt-nowrap', render: (it) => (
      <span title={formatDateTime(it.deleted_at)}>{formatDate(it.deleted_at)}</span>
    ) },
    { key: 'deleted_by', header: 'Borrado por', render: (it) => (it.deleted_by ? `Usuario #${it.deleted_by}` : '—') },
    { key: 'actions', header: '', className: 'col-actions', render: (it) => {
      const isRestoring = restoring === `${it.entity_type}-${it.id}`;
      return (
        <div className="dt-actions">
          <button
            className="btn-icon"
            onClick={() => handleRestore(it)}
            disabled={isRestoring}
            title={isRestoring ? 'Restaurando…' : 'Restaurar'}
            aria-label={`Restaurar ${it.label.toLowerCase()} ${it.title}`}
            style={{ color: 'var(--success)' }}
          >
            <RotateCcw size={16} />
          </button>
        </div>
      );
    } },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Papelera</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
        <button
          className="btn btn-outline"
          onClick={load}
          disabled={loading}
          title="Refrescar lista"
        >
          <RotateCcw size={18} /> {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por título o tipo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-input"
            style={{ width: 220 }}
            value={filterEntity}
            onChange={(e) => setFilterEntity(e.target.value)}
            aria-label="Filtrar por categoría"
          >
            {ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Trash2 size={64} />
            <h3>{items.length === 0 ? 'La papelera está vacía' : 'Nada coincide con tu filtro'}</h3>
            <p>
              {items.length === 0
                ? 'Cuando elimines un cliente, ticket, producto u otro elemento, aparecerá aquí.'
                : 'Prueba con otro texto o cambia el filtro de categoría.'}
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} rowKey={(r) => `${r.entity_type}-${r.id}`} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map((item) => {
              const rowKey = `${item.entity_type}-${item.id}`;
              const Icon = ENTITY_ICON[item.entity_type] || FileText;
              const isRestoring = restoring === rowKey;
              return (
                <div className="item-card" key={rowKey}>
                  <div className="item-card-header">
                    <div className="item-card-main">
                      <div className="item-card-avatar">
                        <Icon size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="item-card-title">{item.title}</div>
                        {!fl.isHidden('id') && <div className="item-card-subtitle">#{item.id}</div>}
                      </div>
                    </div>
                    {!fl.isHidden('label') && <span className="badge badge-gray">{item.label}</span>}
                  </div>

                  {((item.entity_type === 'customer' && !fl.isHidden('invoice_count')) || (!fl.isHidden('deleted_by') && item.deleted_by)) && (
                    <div className="item-card-body">
                      {item.entity_type === 'customer' && !fl.isHidden('invoice_count') && (
                        <div className="item-card-row" style={fl.cardStyle('invoice_count')}>
                          <span className="item-label">Facturas</span>
                          <span>{Number.isInteger(item.invoice_count) ? item.invoice_count : '—'}</span>
                        </div>
                      )}
                      {!fl.isHidden('deleted_by') && item.deleted_by && (
                        <div className="item-card-row" style={fl.cardStyle('deleted_by')}>
                          <span className="item-label">Eliminado por</span>
                          <span>Usuario #{item.deleted_by}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="item-card-footer">
                    {!fl.isHidden('deleted_at') && (
                      <span className="item-card-date" title={formatDateTime(item.deleted_at)}>
                        Eliminado el {formatDate(item.deleted_at)}
                      </span>
                    )}
                    <div className="item-card-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleRestore(item)}
                        disabled={isRestoring}
                        title={isRestoring ? 'Restaurando…' : 'Restaurar'}
                        aria-label={`Restaurar ${item.label.toLowerCase()} ${item.title}`}
                        style={{ color: 'var(--success)' }}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Trash;
